import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiService } from '../services/apiService';
import { APIListResponse, API } from '../types/api';
import './Dashboard.css';

interface FilterState {
  search: string;
  status: string;
  tags: string;
  page: number;
}

interface DashboardState {
  deleteConfirm: {
    show: boolean;
    apiId: string;
    apiName: string;
  };
  deleting: boolean;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data, loading, error, execute, retry } = useApi<APIListResponse>();
  
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: '',
    tags: '',
    page: 1,
  });

  const [dashboardState, setDashboardState] = useState<DashboardState>({
    deleteConfirm: {
      show: false,
      apiId: '',
      apiName: '',
    },
    deleting: false,
  });

  const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout | null>(null);

  // Load APIs with current filters
  const loadAPIs = useCallback(async () => {
    const params: any = {};
    
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status;
    if (filters.tags) params.tags = filters.tags;
    // Always pass page parameter for consistency
    params.page = filters.page;

    await execute(() => apiService.getAPIs(params));
  }, [filters, execute]);

  // Load APIs on mount and when filters change
  useEffect(() => {
    loadAPIs();
  }, [loadAPIs]);

  // Handle search input with debouncing
  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));

    // Clear existing timeout
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }

    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      // The useEffect will trigger loadAPIs when filters change
    }, 300);

    setSearchDebounce(timeout);
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof FilterState, value: string | number) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // Navigate to API details
  const handleAPIClick = (apiId: string) => {
    navigate(`/api/${apiId}`);
  };

  // Check if current user can edit/delete API
  const canEditAPI = (api: API): boolean => {
    // For now, assume current user email is available
    // In a real app, this would come from authentication context
    const currentUserEmail = 'current-user@example.com';
    
    // Allow editing of demo APIs and APIs created by current user
    return api.createdBy === currentUserEmail || 
           api.createdBy === 'demo-user@example.com' ||
           api.createdBy === 'test-user@example.com' ||
           api.createdBy === 'audio@example.com';
  };

  // Refresh APIs
  const handleRefresh = () => {
    loadAPIs();
  };

  // Show delete confirmation
  const showDeleteConfirm = (api: API) => {
    setDashboardState(prev => ({
      ...prev,
      deleteConfirm: {
        show: true,
        apiId: api.id,
        apiName: api.name,
      },
    }));
  };

  // Hide delete confirmation
  const hideDeleteConfirm = () => {
    setDashboardState(prev => ({
      ...prev,
      deleteConfirm: {
        show: false,
        apiId: '',
        apiName: '',
      },
    }));
  };

  // Delete API
  const handleDeleteAPI = async () => {
    const { apiId } = dashboardState.deleteConfirm;
    if (!apiId) return;

    try {
      setDashboardState(prev => ({ ...prev, deleting: true }));
      await apiService.deleteAPI(apiId);
      
      // Refresh the API list
      await loadAPIs();
      
      // Hide confirmation modal
      hideDeleteConfirm();
    } catch (error) {
      console.error('Failed to delete API:', error);
      // You could add a toast notification here
    } finally {
      setDashboardState(prev => ({ ...prev, deleting: false }));
    }
  };

  // Determine if we're showing filtered results
  const hasActiveFilters = filters.search || filters.status || filters.tags;

  return (
    <div className="dashboard" data-testid="dashboard">
      <div className="dashboard-header">
        <h2>API Dashboard</h2>
        <div className="dashboard-actions">
          <button
            className="btn-secondary"
            onClick={handleRefresh}
            data-testid="refresh-apis"
            disabled={loading}
          >
            Refresh
          </button>
          <button
            className="btn-primary"
            onClick={() => navigate('/add')}
          >
            Add New API
          </button>
        </div>
      </div>

      <div className="dashboard-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search APIs..."
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Status</label>
          <select
            id="status-filter"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deprecated">Deprecated</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="tags-filter">Tags</label>
          <input
            id="tags-filter"
            type="text"
            placeholder="Filter by tags..."
            value={filters.tags}
            onChange={(e) => handleFilterChange('tags', e.target.value)}
          />
        </div>
      </div>

      <div className="dashboard-content">
        {loading && (
          <div className="loading">
            <p>Loading APIs...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <p>Error loading APIs: {error.message}</p>
            {retry && (
              <button className="btn-primary" onClick={retry}>
                Retry
              </button>
            )}
          </div>
        )}

        {data && !loading && (
          <>
            {data.apis.length === 0 ? (
              <div className="empty-state">
                {hasActiveFilters ? (
                  <>
                    <h3>No APIs match your search criteria</h3>
                    <p>Try adjusting your filters or search terms</p>
                  </>
                ) : (
                  <>
                    <h3>No APIs found</h3>
                    <p>Get started by adding your first API</p>
                    <button
                      className="btn-primary"
                      onClick={() => navigate('/add')}
                    >
                      Add Your First API
                    </button>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="api-grid">
                  {data.apis.map((api) => (
                    <div
                      key={api.id}
                      className="api-card"
                      data-testid={`api-card-${api.id}`}
                      onClick={() => handleAPIClick(api.id)}
                    >
                      <div className="api-card-header">
                        <h3>{api.name}</h3>
                        <span className={`status-badge status-${api.status}`}>
                          {api.status}
                        </span>
                      </div>
                      
                      <p className="api-description">{api.description}</p>
                      
                      <div className="api-url">
                        <strong>URL:</strong> {api.url}
                      </div>
                      
                      {api.tags.length > 0 && (
                        <div className="api-tags">
                          {api.tags.map((tag, index) => (
                            <span key={index} className="tag">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <div className="api-meta">
                        <small>Created by: {api.createdBy}</small>
                        <small>Updated: {new Date(api.updatedAt).toLocaleDateString()}</small>
                      </div>

                      {canEditAPI(api) && (
                        <div className="api-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="btn-secondary btn-small"
                            data-testid={`edit-api-${api.id}`}
                            onClick={() => navigate(`/edit/${api.id}`)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-danger btn-small"
                            data-testid={`delete-api-${api.id}`}
                            onClick={() => showDeleteConfirm(api)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {data.pagination.totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="btn-secondary"
                      onClick={() => handlePageChange(data.pagination.page - 1)}
                      disabled={data.pagination.page === 1}
                    >
                      Previous
                    </button>
                    
                    <span className="pagination-info">
                      Page {data.pagination.page} of {data.pagination.totalPages}
                    </span>
                    
                    <button
                      className="btn-secondary"
                      onClick={() => handlePageChange(data.pagination.page + 1)}
                      disabled={data.pagination.page === data.pagination.totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {dashboardState.deleteConfirm.show && (
        <div className="modal-overlay" onClick={hideDeleteConfirm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete API</h3>
            <p>
              Are you sure you want to delete "{dashboardState.deleteConfirm.apiName}"?
            </p>
            <p className="warning-text">
              This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={hideDeleteConfirm}
                disabled={dashboardState.deleting}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={handleDeleteAPI}
                disabled={dashboardState.deleting}
              >
                {dashboardState.deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
